import type { BattleInput } from "../core/types";

type InputOwner = "keyboard" | "pointer" | "gamepad";

const KEY_DIRECTIONS: Record<string, readonly [number, number]> = {
  KeyW: [0, -1],
  ArrowUp: [0, -1],
  KeyS: [0, 1],
  ArrowDown: [0, 1],
  KeyA: [-1, 0],
  ArrowLeft: [-1, 0],
  KeyD: [1, 0],
  ArrowRight: [1, 0],
};

export class InputController {
  readonly #pressedKeys = new Set<string>();
  readonly #pauseHandler: () => void;
  #owner: InputOwner = "keyboard";
  #pointerId: number | null = null;
  #pointerOrigin = { x: 0, y: 0 };
  #pointerVector = { x: 0, y: 0 };
  #joystickElement: HTMLElement | null = null;

  constructor(pauseHandler: () => void) {
    this.#pauseHandler = pauseHandler;
    window.addEventListener("keydown", this.#onKeyDown, { passive: false });
    window.addEventListener("keyup", this.#onKeyUp);
    window.addEventListener("blur", this.#onBlur);
  }

  attachJoystick(element: HTMLElement): void {
    this.#joystickElement = element;
    element.addEventListener("pointerdown", this.#onPointerDown);
    element.addEventListener("pointermove", this.#onPointerMove);
    element.addEventListener("pointerup", this.#onPointerUp);
    element.addEventListener("pointercancel", this.#onPointerUp);
  }

  sample(): BattleInput {
    const gamepad = this.#sampleGamepad();
    if (gamepad && Math.hypot(gamepad.moveX, gamepad.moveY) > 0.18) {
      this.#owner = "gamepad";
      return gamepad;
    }

    if (this.#pointerId !== null) {
      this.#owner = "pointer";
      return {
        moveX: this.#pointerVector.x,
        moveY: this.#pointerVector.y,
      };
    }

    let moveX = 0;
    let moveY = 0;
    for (const key of this.#pressedKeys) {
      const direction = KEY_DIRECTIONS[key];
      if (!direction) continue;
      moveX += direction[0];
      moveY += direction[1];
    }
    if (moveX !== 0 || moveY !== 0) this.#owner = "keyboard";
    return { moveX, moveY };
  }

  get owner(): InputOwner {
    return this.#owner;
  }

  destroy(): void {
    window.removeEventListener("keydown", this.#onKeyDown);
    window.removeEventListener("keyup", this.#onKeyUp);
    window.removeEventListener("blur", this.#onBlur);
    if (this.#joystickElement) {
      this.#joystickElement.removeEventListener(
        "pointerdown",
        this.#onPointerDown,
      );
      this.#joystickElement.removeEventListener(
        "pointermove",
        this.#onPointerMove,
      );
      this.#joystickElement.removeEventListener("pointerup", this.#onPointerUp);
      this.#joystickElement.removeEventListener(
        "pointercancel",
        this.#onPointerUp,
      );
    }
  }

  readonly #onKeyDown = (event: KeyboardEvent): void => {
    if (event.code === "Escape" || event.code === "KeyP") {
      if (!event.repeat) this.#pauseHandler();
      event.preventDefault();
      return;
    }
    if (KEY_DIRECTIONS[event.code]) {
      this.#pressedKeys.add(event.code);
      this.#owner = "keyboard";
      event.preventDefault();
    }
  };

  readonly #onKeyUp = (event: KeyboardEvent): void => {
    this.#pressedKeys.delete(event.code);
  };

  readonly #onBlur = (): void => {
    this.#pressedKeys.clear();
    this.#pointerId = null;
    this.#pointerVector = { x: 0, y: 0 };
  };

  readonly #onPointerDown = (event: PointerEvent): void => {
    if (this.#pointerId !== null || !this.#joystickElement) return;
    this.#pointerId = event.pointerId;
    this.#pointerOrigin = { x: event.clientX, y: event.clientY };
    this.#pointerVector = { x: 0, y: 0 };
    try {
      this.#joystickElement.setPointerCapture(event.pointerId);
    } catch {
      // Synthetic events and a few embedded browsers may not support capture.
    }
    this.#joystickElement.dataset.active = "true";
    this.#owner = "pointer";
  };

  readonly #onPointerMove = (event: PointerEvent): void => {
    if (event.pointerId !== this.#pointerId) return;
    const maximum = 46;
    const x = event.clientX - this.#pointerOrigin.x;
    const y = event.clientY - this.#pointerOrigin.y;
    const magnitude = Math.hypot(x, y);
    const scale = magnitude > maximum ? maximum / magnitude : 1;
    this.#pointerVector = {
      x: (x * scale) / maximum,
      y: (y * scale) / maximum,
    };
    this.#joystickElement?.style.setProperty(
      "--stick-x",
      `${this.#pointerVector.x * 32}px`,
    );
    this.#joystickElement?.style.setProperty(
      "--stick-y",
      `${this.#pointerVector.y * 32}px`,
    );
  };

  readonly #onPointerUp = (event: PointerEvent): void => {
    if (event.pointerId !== this.#pointerId) return;
    this.#pointerId = null;
    this.#pointerVector = { x: 0, y: 0 };
    if (this.#joystickElement) {
      this.#joystickElement.dataset.active = "false";
      this.#joystickElement.style.setProperty("--stick-x", "0px");
      this.#joystickElement.style.setProperty("--stick-y", "0px");
    }
  };

  #sampleGamepad(): BattleInput | null {
    const pads = navigator.getGamepads?.();
    const pad = pads?.[0];
    if (!pad) return null;
    const moveX = deadzone(pad.axes[0] ?? 0);
    const moveY = deadzone(pad.axes[1] ?? 0);
    return { moveX, moveY };
  }
}

function deadzone(value: number): number {
  if (Math.abs(value) < 0.16) return 0;
  return Math.sign(value) * ((Math.abs(value) - 0.16) / 0.84);
}
